import React from 'react';
import styled from 'styled-components';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import apiFetch from '../util/fetch-utils';
import SearchIcon from '@material-ui/icons/Search';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { toaster } from 'evergreen-ui';

/**
 * UriLink — single component for rendering a URL with three interaction points:
 *
 *  1. URL text (button)   → navigates to the Monitored URI page for this URL within the
 *                           current workspace, using the API lookup below.
 *  2. Magnifying glass    → same as text: navigates to the Monitored URI page.
 *  3. External link (↗)   → opens the actual URL in a new browser tab.
 *
 * Monitored URI lookup:
 *   GET /api/monitored-uris/search/searchSingleMonitoredUri?uri=<url>&workspaceNumber=<n>
 *   On success  → history.push to /ws/:workspaceNumber/monitored-uris/:id, carrying the caller's
 *                 view state so the page opens on the same dates the caller was looking at
 *   On 404/fail → falls back to opening the URL in a new tab
 *
 * Props:
 *   url          {string}  The URL to display and link.
 *   displayText  {string}  Optional override for the visible text (defaults to url).
 */
export function useNavigateToMonitoredUri() {
    const { workspaceNumber } = useParams();
    const history = useHistory();
    const location = useLocation();
    return async (url, viewState) => {
        if (!workspaceNumber) { window.open(url, '_blank', 'noopener noreferrer'); return; }
        const res = await apiFetch(`/api/monitored-uris/search/searchSingleMonitoredUri?uri=${encodeURIComponent(url)}&workspaceNumber=${workspaceNumber}`);
        if (res.ok) {
            const data = await res.json();
            const date = new URLSearchParams(location.search).get('date');
            const params = new URLSearchParams({ ...(date ? { date } : {}), ...(viewState || {}) });
            history.push(`/ws/${workspaceNumber}/monitored-uris/${data.id}${params.toString() ? '?' + params : ''}`);
        } else {
            toaster.warning('Page not in this workspace', {
                description: 'This URL is not present in this workspace as it has never been crawled directly. ' +
                    'Use the external link icon to open it directly.',
                duration: 4,
            });
        }
    };
}

export default function UriLink({ url, displayText }) {
    const navigateTo = useNavigateToMonitoredUri();
    if (!url) return null;
    return (
        <Wrap>
            <UrlBtn onClick={() => navigateTo(url)} title="View in SEO tool">
                {displayText || url}
            </UrlBtn>
            <IconBtn onClick={() => navigateTo(url)} title="View in SEO tool">
                <SearchIcon style={{ fontSize: '0.75rem' }} />
            </IconBtn>
            <IconBtn as="a" href={url} target="_blank" rel="noopener noreferrer" title="Open in new tab">
                <OpenInNewIcon style={{ fontSize: '0.75rem' }} />
            </IconBtn>
        </Wrap>
    );
}

const Wrap = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    min-width: 0;
`;

const UrlBtn = styled.button`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: #6366f1;
    text-align: left;
    word-break: break-all;
    cursor: pointer;
    &:hover { text-decoration: underline; }
`;

const IconBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #cbd5e1;
    cursor: pointer;
    padding: 0;
    text-decoration: none;
    transition: background 120ms, color 120ms;
    &:hover { background: #ede9fe; color: #6366f1; }
`;
